import React, { useState, useEffect } from 'react';
import { Button } from '@roger/ui-components';
import { Card, CardContent, CardHeader, CardTitle } from '@roger/ui-components';
import { Badge } from '@roger/ui-components';
import {
  ArrowLeft,
  Download,
  FileText,
  Eye,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getPatientDocuments,
  getPatientDocumentUrl,
  client,
} from '../services/api';
import PDFViewer from '../components/PDFViewer';

interface DocumentDetails {
  id: string;
  patient_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  document_status: string;
  is_processed: boolean;
  requires_signature: boolean;
  is_signed: boolean;
  signed_by?: string;
  signed_at?: string;
  created_at: string;
  document_type?: string | null;
}

const DocumentViewer: React.FC = () => {
  const { patientId, documentId } = useParams<{
    patientId: string;
    documentId: string;
  }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<DocumentDetails | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDocumentDetails = async () => {
      if (!patientId || !documentId) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch all patient documents to find the specific one
        const documents = await getPatientDocuments({
          path: { patient_id: patientId },
          client,
        });
        if (documents && documents.data) {
          const foundDocument = documents.data.find(
            doc => doc.id === documentId
          );

          if (!foundDocument) {
            setError('Document not found');
            return;
          }

          // Convert to DocumentDetails format
          const documentDetails: DocumentDetails = {
            id: foundDocument.id,
            patient_id: foundDocument.patient_id,
            file_name: foundDocument.file_name,
            file_path: foundDocument.file_path,
            file_size: foundDocument.file_size,
            mime_type: foundDocument.mime_type,
            document_status: foundDocument.document_status,
            is_processed: foundDocument.is_processed,
            requires_signature: foundDocument.requires_signature,
            is_signed: foundDocument.is_signed,
            signed_by: foundDocument.signed_by || undefined,
            signed_at: foundDocument.signed_at || undefined,
            created_at: new Date().toISOString(), // Not available in API response
            document_type: foundDocument.document_type || null,
          };

          setDocument(documentDetails);

          // Get presigned URL for the document
          try {
            const urlResponse = await getPatientDocumentUrl({
              path: { patient_id: patientId, document_id: documentId },
              client,
            });
            if (urlResponse && urlResponse.data) {
              setDocumentUrl(urlResponse.data as string);
            }
          } catch (urlError) {
            console.warn('Failed to get document URL:', urlError);
            setDocumentUrl(null);
          }
        } else {
          setError('No documents found');
        }
      } catch (err) {
        console.error('Error fetching document details:', err);
        setError('Failed to load document details');
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentDetails();
  }, [patientId, documentId]);

  const handleDownload = () => {
    if (documentUrl) {
      const link = window.document.createElement('a');
      link.href = documentUrl;
      link.download = document?.file_name || 'document';
      link.click();
    }
  };

  const handleOpenInNewTab = () => {
    if (documentUrl) {
      window.open(documentUrl, '_blank');
    }
  };

  const handleBackToPatient = () => {
    navigate(`/patients/${patientId}`);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'signed':
        return 'default';
      case 'under_review':
        return 'secondary';
      case 'pending_signature':
        return 'outline';
      case 'rejected':
        return 'destructive';
      case 'expired':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const canViewDocument = (mimeType: string): boolean => {
    return [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'text/plain',
    ].includes(mimeType);
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
          <p className='text-muted-foreground'>Loading document...</p>
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertCircle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Document</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Document not found'}
          </p>
          <Button onClick={handleBackToPatient} variant='outline'>
            <ArrowLeft className='h-4 w-4' />
            Back to Patient
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleBackToPatient}
            className='flex items-center'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Patient
          </Button>
          <div>
            <h1 className='text-2xl font-bold'>Document Viewer</h1>
            <p className='text-muted-foreground'>
              Viewing: {document.file_name}
            </p>
          </div>
        </div>
        <div className='flex gap-2'>
          <Button onClick={handleDownload} variant='outline'>
            <Download className='h-4 w-4' />
            Download
          </Button>
          <Button onClick={handleOpenInNewTab}>
            <ExternalLink className='h-4 w-4' />
            Open in New Tab
          </Button>
        </div>
      </div>

      {/* Document Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <FileText className='h-5 w-5' />
            Document Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <h4 className='font-medium text-sm text-muted-foreground mb-1'>
                File Name
              </h4>
              <p className='text-sm'>{document.file_name}</p>
            </div>
            <div>
              <h4 className='font-medium text-sm text-muted-foreground mb-1'>
                File Size
              </h4>
              <p className='text-sm'>{formatFileSize(document.file_size)}</p>
            </div>
            <div>
              <h4 className='font-medium text-sm text-muted-foreground mb-1'>
                Document Type
              </h4>
              <p className='text-sm'>{document.document_type || 'Unknown'}</p>
            </div>
            <div>
              <h4 className='font-medium text-sm text-muted-foreground mb-1'>
                Status
              </h4>
              <Badge variant={getStatusVariant(document.document_status)}>
                {document.document_status}
              </Badge>
            </div>
            {document.requires_signature && (
              <>
                <div>
                  <h4 className='font-medium text-sm text-muted-foreground mb-1'>
                    Signature Required
                  </h4>
                  <p className='text-sm'>
                    {document.is_signed ? 'Signed' : 'Pending'}
                  </p>
                </div>
                {document.signed_by && (
                  <div>
                    <h4 className='font-medium text-sm text-muted-foreground mb-1'>
                      Signed By
                    </h4>
                    <p className='text-sm'>{document.signed_by}</p>
                  </div>
                )}
                {document.signed_at && (
                  <div>
                    <h4 className='font-medium text-sm text-muted-foreground mb-1'>
                      Signed At
                    </h4>
                    <p className='text-sm'>{formatDate(document.signed_at)}</p>
                  </div>
                )}
              </>
            )}
            <div>
              <h4 className='font-medium text-sm text-muted-foreground mb-1'>
                Created At
              </h4>
              <p className='text-sm'>{formatDate(document.created_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Preview */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Eye className='h-5 w-5' />
            Document Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          {document.mime_type === 'application/pdf' ? (
            <PDFViewer
              url={documentUrl}
              loading={loading}
              error={error}
              onDownload={handleDownload}
              onOpenInNewTab={handleOpenInNewTab}
              maxWidth={800}
              maxHeight={800}
              defaultScale={1.0}
            />
          ) : canViewDocument(document.mime_type) ? (
            <div className='text-center py-8 border-2 border-dashed border-border rounded-lg'>
              <FileText className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
              <p className='text-muted-foreground mb-4'>
                Preview for this file type will be available soon
              </p>
              <Button onClick={handleOpenInNewTab} variant='outline'>
                <ExternalLink className='h-4 w-4' />
                Open Document
              </Button>
            </div>
          ) : (
            <div className='text-center py-8 border-2 border-dashed border-border rounded-lg'>
              <FileText className='h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50' />
              <p className='text-muted-foreground mb-2'>
                Preview not available for this file type
              </p>
              <p className='text-sm text-muted-foreground mb-4'>
                Please download the file to view its contents
              </p>
              <Button onClick={handleDownload} variant='outline'>
                <Download className='h-4 w-4' />
                Download File
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentViewer;
